import React from "react";
import { Button, Tooltip } from "@mui/material";
import { SouthRounded, NorthRounded } from "@mui/icons-material";

/**
 * Prominent, labeled sort-order toggle (Newest ⇄ Oldest).
 *
 * Reports used to expose ordering only as a subtle arrow on a column header,
 * which users couldn't find. This renders an obvious, primary-tinted button
 * that shows the CURRENT order in words ("Newest First" / "Oldest First") with
 * a matching ↓/↑ arrow, and flips on click. Drop it in a table toolbar.
 *
 * Props:
 *   dir: "asc" | "desc"        current direction (desc = newest first)
 *   onToggle: () => void       flip handler (parent owns the state)
 *   descLabel / ascLabel       localized labels for each direction
 *   tooltip                    localized hover hint
 */
const OrderToggle = ({
  dir = "desc",
  onToggle,
  descLabel = "Newest First",
  ascLabel = "Oldest First",
  tooltip = "Toggle order",
}) => {
  const isAsc = dir === "asc";
  return (
    <Tooltip title={tooltip}>
      <Button
        onClick={onToggle}
        variant="outlined"
        size="small"
        disableElevation
        startIcon={isAsc ? <NorthRounded /> : <SouthRounded />}
        sx={{
          // Neutral secondary-button style — matches the other toolbar actions
          // (e.g. Export, "Fix Invoice Payments") so it feels native. Visibility
          // comes from the clear label + the primary-tinted arrow, not a bold fill.
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 500,
          fontSize: "13px",
          px: 2,
          py: 0.8,
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          color: "#374151",
          borderColor: "#e5e7eb",
          backgroundColor: "var(--white-color)",
          "& .MuiButton-startIcon": { color: "var(--primary-color)" },
          "&:hover": {
            borderColor: "#d1d5db",
            backgroundColor: "#f9fafb",
          },
        }}
      >
        {isAsc ? ascLabel : descLabel}
      </Button>
    </Tooltip>
  );
};

export default OrderToggle;
